/**
 * TransactionListItem - MOBILE OPTIMIZED
 * 
 * Versão completamente otimizada para mobile:
 * - Touch targets de 44px mínimo
 * - Informações essenciais apenas
 * - Swipe actions (futuro)
 * - Performance otimizada com React.memo
 */

import { memo } from 'react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { 
  MoreVertical, 
  Pencil, 
  Trash2, 
  Calendar, 
  Tag,
  CheckCircle2,
  Clock,
  RefreshCw,
  Copy,
  Building2,
} from 'lucide-react';
import { formatCurrency, formatDate } from '../../../lib/utils/format';
import { useCompanyCurrency } from '../../../hooks/useCompanyCurrency';
import { cn } from '../../../components/ui/utils';
import { TRANSACTION_TYPE_CONFIG } from '../../../lib/constants/transaction';
import type { CashFlowEntry, Company } from '../../../types/database';
import { motion } from 'motion/react';

interface TransactionListItemProps {
  transaction: CashFlowEntry;
  onEdit: (transaction: CashFlowEntry) => void;
  onDelete: (id: string) => void;
  onDuplicate?: (transaction: CashFlowEntry) => void;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  isMobile?: boolean;
  companyName?: string;
  showCompany?: boolean;
  canEdit?: boolean; // NEW: Permission to edit
  isReconciled?: boolean; // NEW: Is this transaction reconciled with bank statement?
}

function TransactionListItemComponent({
  transaction,
  onEdit,
  onDelete,
  onDuplicate,
  isSelected = false,
  onToggleSelect,
  isMobile = false,
  companyName,
  showCompany = false,
  canEdit = true, // Default true for backward compatibility
  isReconciled = false, // Default false
}: TransactionListItemProps) {
  const companyCurrency = useCompanyCurrency();
  const config = TRANSACTION_TYPE_CONFIG[transaction.entry_type];
  const Icon = config.icon;

  // ✅ Priorizar payment_date (data de pagamento), depois due_date (vencimento), depois entry_date
  const relevantDate = transaction.payment_date || transaction.due_date || transaction.entry_date;

  // Mobile: Simplified layout
  if (isMobile) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -50 }}
        className={cn(
          'flex items-center gap-3 p-3 rounded-lg border-2 transition-all',
          'active:scale-[0.98] active:bg-accent/50',
          isSelected && 'ring-2 ring-primary ring-offset-2',
          'border-border'
        )}
      >
        {/* Checkbox de seleção */}
        {onToggleSelect && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect(transaction.id)}
            onClick={(e) => e.stopPropagation()}
            className="shrink-0"
            aria-label="Selecionar lançamento"
          />
        )}

        {/* Icon */}
        <div 
          className={cn('flex items-center justify-center w-10 h-10 rounded-lg shrink-0', config.bgColor, canEdit && 'cursor-pointer')}
          onClick={canEdit ? () => onEdit(transaction) : undefined}
        >
          <Icon className={cn('w-5 h-5', config.color)} />
        </div>

        {/* Content - Vertical stacking for mobile */}
        <div className={cn('flex-1 min-w-0', canEdit && 'cursor-pointer')} onClick={canEdit ? () => onEdit(transaction) : undefined}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="font-semibold text-sm truncate leading-tight">
              {transaction.description}
            </p>
            <span className={cn('font-bold text-base shrink-0 tabular-nums', config.color)}>
              {config.sign}{formatCurrency(Number(transaction.amount), companyCurrency)}
            </span>
          </div>

          {/* Meta info - Compact */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {transaction.category && (
              <div className="flex items-center gap-1 truncate">
                <Tag className="w-3 h-3 shrink-0" />
                <span className="truncate">{transaction.category}</span>
              </div>
            )}
            {relevantDate && (
              <>
                <span>•</span>
                <span>{formatDate(relevantDate, 'short')}</span>
              </>
            )}
          </div>

          {/* Status badges - Small */}
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <Badge
              variant={transaction.payment_status === 'confirmado' ? 'default' : 'secondary'}
              className={cn(
                "text-[10px] h-5 px-1.5 flex items-center gap-1",
                transaction.payment_status === 'previsão' && 'bg-amber-100 text-amber-700 border-amber-300'
              )}
            >
              {transaction.payment_status === 'confirmado' ? (
                <><CheckCircle2 className="w-2.5 h-2.5" /> Confirmado</>
              ) : (
                <><Clock className="w-2.5 h-2.5" /> Planejado</>
              )}
            </Badge>
            {isReconciled && (
              <Badge
                variant="default"
                className="text-[10px] h-5 px-1.5 flex items-center gap-1 bg-success-600 hover:bg-success-700"
              >
                <CheckCircle2 className="w-2.5 h-2.5" /> Conciliado
              </Badge>
            )}
            {showCompany && companyName && (
              <Badge
                variant="outline"
                className="text-[10px] h-5 px-1.5 flex items-center gap-1"
              >
                <Building2 className="w-2.5 h-2.5" />
                {companyName}
              </Badge>
            )}
            {transaction.is_recurring && (
              <RefreshCw className="w-3 h-3 text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Actions - Touch friendly (44px) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11 shrink-0"
            >
              <MoreVertical className="w-5 h-5" />
              <span className="sr-only">Ações</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {canEdit && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(transaction);
                }}
                className="py-3"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Editar
              </DropdownMenuItem>
            )}
            
            {onDuplicate && canEdit && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate(transaction);
                }}
                className="py-3"
              >
                <Copy className="w-4 h-4 mr-2" />
                Duplicar
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />
            
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onDelete(transaction.id);
              }}
              className="text-destructive focus:text-destructive py-3"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </motion.div>
    );
  }

  // Desktop: Rich layout (mantém versão existente otimizada)
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      layout
      className="group relative"
    >
      <div
        className={cn(
          'flex items-start gap-4 p-4 rounded-xl border-2 transition-all duration-200',
          'hover:shadow-lg hover:border-primary/30',
          'active:scale-[0.99]',
          isSelected && 'ring-2 ring-primary ring-offset-2',
          config.hoverBorder,
          'border-border'
        )}
      >
        {/* Checkbox de seleção */}
        {onToggleSelect && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect(transaction.id)}
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 mt-1"
            aria-label="Selecionar lançamento"
          />
        )}

        {/* Icon with animation */}
        <motion.div 
          className={cn(
            'flex items-center justify-center w-12 h-12 rounded-xl shrink-0 transition-all',
            config.bgColor,
            canEdit && 'cursor-pointer'
          )}
          whileHover={canEdit ? { scale: 1.1, rotate: 5 } : undefined}
          whileTap={canEdit ? { scale: 0.95 } : undefined}
          onClick={canEdit ? () => onEdit(transaction) : undefined}
        >
          <Icon className={cn('w-6 h-6', config.color)} />
        </motion.div>

        {/* Content */}
        <div className={cn('flex-1 min-w-0 space-y-2', canEdit && 'cursor-pointer')} onClick={canEdit ? () => onEdit(transaction) : undefined}>
          {/* Title Row */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0 space-y-1">
              <p className="font-semibold truncate leading-tight text-base">
                {transaction.description}
              </p>
              
              {/* Category & Date Meta */}
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                {transaction.category && (
                  <div className="flex items-center gap-1">
                    <Tag className="w-3.5 h-3.5" />
                    <span className="text-xs">{transaction.category}</span>
                  </div>
                )}
                
                {relevantDate && (
                  <>
                    <span className="text-muted-foreground/50">•</span>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      <span className="text-xs">{formatDate(relevantDate, 'short')}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Amount */}
            <motion.div 
              className={cn('font-bold text-xl shrink-0 tabular-nums', config.color)}
              initial={{ scale: 1 }}
              whileHover={{ scale: 1.05 }}
            >
              {config.sign} {formatCurrency(Number(transaction.amount), companyCurrency)}
            </motion.div>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant={transaction.payment_status === 'confirmado' ? 'default' : 'secondary'}
              className={cn(
                "text-xs h-6 flex items-center gap-1.5",
                transaction.payment_status === 'previsão' && 'bg-amber-100 text-amber-700 border-amber-300'
              )}
            >
              {transaction.payment_status === 'confirmado' ? (
                <><CheckCircle2 className="w-3 h-3" /> Confirmado</>
              ) : (
                <><Clock className="w-3 h-3" /> Planejado</>
              )}
            </Badge>

            {isReconciled && (
              <Badge
                variant="default"
                className="text-xs h-6 flex items-center gap-1.5 bg-success-600 hover:bg-success-700"
              >
                <CheckCircle2 className="w-3 h-3" /> Conciliado
              </Badge>
            )}

            {transaction.is_recurring && (
              <Badge variant="outline" className="text-xs h-6 flex items-center gap-1.5">
                <RefreshCw className="w-3 h-3" /> Recorrente
              </Badge>
            )}

            {showCompany && companyName && (
              <Badge variant="outline" className="text-xs h-6 flex items-center gap-1.5">
                <Building2 className="w-3 h-3" /> {companyName}
              </Badge>
            )}
          </div>
        </div>

        {/* Quick Actions Desktop */}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(transaction);
              }}
              aria-label="Editar lançamento"
            >
              <Pencil className="w-4 h-4" />
            </Button>
          )}

          {onDuplicate && canEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate(transaction);
              }}
              aria-label="Duplicar lançamento"
            >
              <Copy className="w-4 h-4" />
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                aria-label="Mais ações"
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(transaction.id);
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </motion.div>
  );
}

// Memoize component to prevent unnecessary re-renders
export const TransactionListItem = memo(
  TransactionListItemComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.transaction.id === nextProps.transaction.id &&
      prevProps.transaction.updated_at === nextProps.transaction.updated_at &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.onToggleSelect === nextProps.onToggleSelect
    );
  }
);
